import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

// Block User Mutations
const BLOCK_USER = `
  mutation BlockUser($blockedUserID: ID!, $reason: String) {
    blockUser(blockedUserID: $blockedUserID, reason: $reason) {
      success
      blockedUser {
        id
        blockerID
        blockedID
        reason
        createdAt
      }
      message
    }
  }
`;

const UNBLOCK_USER = `
  mutation UnblockUser($blockedUserID: ID!) {
    unblockUser(blockedUserID: $blockedUserID) {
      success
      message
    }
  }
`;

const GET_BLOCKED_USERS = `
  query GetBlockedUsers($limit: Int, $nextToken: String) {
    getBlockedUsers(limit: $limit, nextToken: $nextToken) {
      success
      blockedUsers {
        id
        blockerID
        blockedID
        reason
        createdAt
      }
      nextToken
      message
    }
  }
`;

const GET_USER_BY_ID = `
  query GetUser($id: ID!) {
    getUser(id: $id) {
      id
      username
      displayName
      avatar
    }
  }
`;

export const blockUserService = {
  async blockUser(blockedUserID, reason = null) {
    try {
      const response = await getClient().graphql({
        query: BLOCK_USER,
        variables: { blockedUserID, reason }
      });

      return response.data.blockUser;
    } catch (error) {
      throw new Error(error.message || 'Failed to block user');
    }
  },

  async unblockUser(blockedUserID) {
    try {
      const response = await getClient().graphql({
        query: UNBLOCK_USER,
        variables: { blockedUserID }
      });

      return response.data.unblockUser;
    } catch (error) {
      console.error('Unblock user error:', error);
      throw new Error(error.message || 'Failed to unblock user');
    }
  },

  async getBlockedUsers(limit = 20, nextToken = null) {
    try {
      const response = await getClient().graphql({
        query: GET_BLOCKED_USERS,
        variables: { limit, nextToken }
      });

      return response.data.getBlockedUsers;
    } catch (error) {
      throw new Error(error.message || 'Failed to get blocked users');
    }
  },

  async getBlockedUsersWithDetails(limit = 20, nextToken = null) {
    try {
      const blockedUsersResult = await this.getBlockedUsers(limit, nextToken);
      
      if (!blockedUsersResult.success) {
        return blockedUsersResult;
      }

      // Fetch user details for each blocked user
      const blockedUsersWithDetails = await Promise.all(
        blockedUsersResult.blockedUsers.map(async (blockRecord) => {
          try {
            const userResponse = await getClient().graphql({
              query: GET_USER_BY_ID,
              variables: { id: blockRecord.blockedID }
            });

            return {
              ...blockRecord,
              blockedUser: userResponse.data.getUser
            };
          } catch (error) {
            return {
              ...blockRecord,
              blockedUser: {
                id: blockRecord.blockedID,
                username: 'Unknown User',
                displayName: 'Unknown User',
                avatar: null
              }
            };
          }
        })
      );

      return {
        ...blockedUsersResult,
        blockedUsers: blockedUsersWithDetails
      };
    } catch (error) {
      throw new Error(error.message || 'Failed to get blocked users with details');
    }
  },

  async isUserBlocked(userID, blockedUsers = null) {
    try {
      // If blockedUsers list is provided, check locally
      if (blockedUsers) {
        return blockedUsers.some(block => block.blockedID === userID);
      }

      // Otherwise fetch and check
      const result = await this.getBlockedUsers(100);
      return result.success && result.blockedUsers.some(block => block.blockedID === userID);
    } catch (error) {
      return false;
    }
  },

  async isBlockedByUser(otherUserID) {
    try {
      const currentUser = await getCurrentUser();
      const currentUserID = currentUser.userId;
      
      const query = `
        query CheckIfBlocked($blockerID: ID!, $blockedID: ID!) {
          listBlockedUsers(
            filter: {
              and: [
                { blockerID: { eq: $blockerID } },
                { blockedID: { eq: $blockedID } }
              ]
            }
            limit: 1
          ) {
            items {
              id
              blockerID
              blockedID
            }
          }
        }
      `;
      
      const response = await getClient().graphql({
        query,
        variables: { 
          blockerID: otherUserID,
          blockedID: currentUserID
        }
      });
      
      return response.data.listBlockedUsers.items.length > 0;
    } catch (error) {
      return false;
    }
  }
};